'use client';

import type { ProjectType } from '~/model/ProjectType';
import classNames from 'classnames';
import { useLocale, useTranslations } from 'next-intl';
import { useState } from 'react';
import { useProjectFollowContext } from '@/app/[locale]/(entity)/components/provider/ProjectFollowProvider';
import { Follow } from '@/app/components/Follow/Follow';
import { HeaderBanner } from '@/app/components/HeaderBanner';
import { MainColumn } from '@/app/components/MainColumn';
import { Share } from '@/app/components/Share/Share';
import { Translate } from '@/app/components/Translate/Translate';
import { getEntityTranslationVersion, getLangAttributeValue } from '@/app/utils/entities/translation';
import { Author } from '~/components/commons/Author/Author';
import { PictureInsert } from '~/components/commons/PictureInsert/PictureInsert';
import { VideoInsert } from '~/components/commons/VideoInsert/VideoInsert';
import appConst, { ComponentTranslationStatus } from '~/const/appConst';
import { AcceptLanguage } from '~/const/locale';
import { ProjectDescription } from './ProjectDescription';

export const ProjectHeader = ({ project }: { project: ProjectType }) => {
  const locale = useLocale();
  const t = useTranslations('GLOBAL');

  const { isFollowedBtnDisplayed } = useProjectFollowContext();

  const [isHidden, setIsHidden] = useState(true);
  const [projectTranslated, setProjectTranslated] = useState(false);

  const userLanguage = AcceptLanguage[locale];

  const originalLocale = project?.original_locale;

  const isEnTranslationValid
    = project?.translations?.en_GB?.status
      === ComponentTranslationStatus.valid;

  const projectVersion = getEntityTranslationVersion({
    entity: project,
    entityTranslated: projectTranslated,
    isEnTranslationValid,
    originalLocale,
    userLanguage,
  });

  const isValidTranslated
    = projectVersion?.status === ComponentTranslationStatus.valid;

  const langValue = getLangAttributeValue(
    project.original_locale,
    userLanguage,
    projectTranslated,
  );

  const {
    video_url: projectVideo = null,
    media: projectMedia = null,
    owner: projectOwner = null,
  } = project || {};

  const getTranslationProject = () => {
    setProjectTranslated(!projectTranslated);
  };

  const { body, title } = !projectTranslated ? project : projectVersion;

  return (
    <section className="project-detail" data-testid="project-detail">
      <MainColumn>
        <HeaderBanner label={t('ALL_PROJECTS')} href={appConst.staticRoutes.projects} />
        <div
          className={classNames('project-detail__wrapper', {
            'project-detail__wrapper--is-shrunk': isHidden,
          })}
        >
          <div
            className={classNames('project-detail__content', {
              'project-detail__content--is-shrunk': isHidden,
              'project-detail__content--with-translation':
                (userLanguage !== originalLocale && !isValidTranslated)
                || projectTranslated,
            })}
          >
            <div className="project-detail__title">
              <h1 lang={langValue} className="vp-title-m">
                {title}
              </h1>
            </div>
            <Share entity={project} iconAlone />
            <div className="project-detail__media">
              {projectVideo
                ? (
                    <VideoInsert url={projectVideo} title={title} />
                  )
                : (
                    projectMedia && (
                      <PictureInsert
                        priority
                        url={projectMedia.content_url}
                      />
                    )
                  )}
            </div>
            <Translate
              getTranslation={getTranslationProject}
              originalLanguage={originalLocale}
              userLanguage={AcceptLanguage[locale]}
              translated={projectTranslated}
              entityUri={project['@id']}
              isValidTranslated={isValidTranslated}
              isEnTranslationValid={isEnTranslationValid}
            />
            <ProjectDescription
              body={body}
              isHidden={isHidden}
              langValue={langValue}
              handleClick={() => setIsHidden(!isHidden)}
            />
            <div className="project-detail__author">
              <Author user={projectOwner} />
              {isFollowedBtnDisplayed && (
                <div className="project-detail__follow">
                  <Follow />
                </div>
              )}
            </div>
          </div>
        </div>
      </MainColumn>
    </section>
  );
};
